import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { Icon, Rating } from '../../features/brand/Primitives';
import DemoModal from '../../features/demo/DemoModal';
import dataService from '../../services/dataService';
import { restaurantPhotoUrl } from '../../services/discoveryApi';
import { useI18n } from '../../features/i18n';
import { CategoryPicker, LivePreview } from '../../screens/LandingScreen';
import { paletteForTag } from '../../features/discovery/taxonomy';
import '../../features/brand/cwm-tokens.css';

/**
 * Unified go-live Landing (v3 — the "no slop" rebuild).
 *
 * Four below-the-fold sections. Each is a single hero visual with motion,
 * not a wall of text.
 *
 *   1. Hero (chat preview, category picker, stats)  ← kept as-is
 *   2. "What safe looks like"  — featured card w/ source badges orbiting in
 *   3. "Swipe to trust it"     — live mini-deck, auto-cycling
 *   4. "Built for your table"  — product message (no testimonials until we
 *                                have real, consented ones)
 *   5. "Free forever"          — two pricing peeks, side by side
 *   6. Final CTA gradient band
 *
 * Every restaurant card and the restaurant count come from the live
 * /listRestaurants response. When it can't be loaded, the count and the
 * cards are hidden rather than replaced with made-up ones.
 */

const EASE = [0.22, 1, 0.36, 1];
// Single source of truth for section vertical rhythm. Every band uses
// the same padding — no per-section overrides — so the page cadence
// reads as "chapter / chapter / chapter" rather than "random gaps."
const BAND_PAD = '88px 48px';

export default function UnifiedLanding() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [demoOpen, setDemoOpen] = useState(false);
  const onGetStarted = () => navigate('/unified/discover');
  const onWatchDemo = () => setDemoOpen(true);

  // Real directory data: the total for the stats, and a handful of rows
  // for the featured card + mini-deck.
  const [restaurantTotal, setRestaurantTotal] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.listRestaurants();
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data?.restaurants || []);
        const total = Number.isFinite(data?.total) ? data.total : rows.length;
        setRestaurantTotal(total > 0 ? total : null);
        setRestaurants(rows);
      } catch {
        // Leave the count and cards hidden.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const showcase = useMemo(() => pickShowcase(restaurants), [restaurants]);
  const totalLabel = restaurantTotal ? restaurantTotal.toLocaleString() : null;

  return (
    <AppShell hero dense footer={true}>
      <div className="cwm-app" style={{ position: 'relative', overflow: 'hidden' }}>

        {/* ============================================================
            1. HERO — branded blue gradient, ends on solid cwm-blue-pale
            so the boundary between hero and the next section is a clean
            single-color seam (not a "fading to nothing" drop-off).
            ============================================================ */}
        <section style={{
          position: 'relative',
          zIndex: 2,
          padding: '32px 48px 72px',
          background:
            'linear-gradient(160deg, var(--cwm-blue-deep, #0F7FE8) 0%, var(--cwm-blue, #1E9BFF) 38%, var(--cwm-blue-light, #BFE5FF) 82%, var(--cwm-blue-pale, #E5F3FF) 100%)',
        }} className="cwm-land-hero">
          <style>{landingResponsiveCss}</style>
          <LivePills />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 0.88fr',
            columnGap: 40,
            rowGap: 28,
            alignItems: 'start',
            maxWidth: 1220, margin: '0 auto',
          }} className="cwm-land-hero-grid">
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '5px 12px', borderRadius: 999,
                background: 'rgba(255,255,255,0.95)',
                color: 'var(--cwm-blue-deep, #0F7FE8)',
                fontSize: 11, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase',
                border: '1px solid rgba(255,255,255,0.35)',
                boxShadow: '0 6px 16px rgba(10,37,64,0.1)',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--cwm-blue-deep, #0F7FE8)', animation: 'cwmLandDot 1.4s infinite' }}/>
                {t('landing.eyebrow')}
              </div>

              <h1 style={{
                fontSize: 62, fontWeight: 900, letterSpacing: '-0.025em', lineHeight: 1.02,
                color: '#fff', margin: '16px 0 20px',
                textShadow: '0 4px 22px rgba(10,37,64,0.45), 0 1px 2px rgba(10,37,64,0.25)',
              }} className="cwm-land-h1">
                {t('landing.headline_l1')}
                <br />
                {t('landing.headline_l2')}
                {t('landing.headline_l2') ? ' ' : ''}
                <span style={{ color: '#CFE8FF', fontStyle: 'italic', display: 'inline-block' }}>
                  {t('landing.headline_emphasis')}
                </span>.
              </h1>

              <p style={{
                fontSize: 18, fontWeight: 600, lineHeight: 1.55,
                color: '#fff',
                textShadow: '0 1px 2px rgba(10,37,64,0.3)',
                maxWidth: 520, margin: 0,
              }}>
                {t('landing.sub')}
              </p>

              <CategoryPicker onGetStarted={onGetStarted} />

              <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
                <button className="cwm-btn" onClick={onGetStarted}
                        style={{ fontSize: 15, padding: '14px 26px', fontWeight: 900, boxShadow: '0 14px 36px rgba(10,37,64,0.32)' }}>
                  <Icon name="explore" size={18} color="#fff" />
                  {t('landing.cta_primary')}
                </button>
                <button type="button" onClick={onWatchDemo}
                        style={{
                          fontSize: 14, padding: '13px 20px', borderRadius: 999,
                          background: 'rgba(255,255,255,0.14)', color: '#fff',
                          border: '1.5px solid rgba(255,255,255,0.45)',
                          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                          fontFamily: 'var(--cwm-font)', fontWeight: 700,
                          cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 8,
                          textShadow: '0 1px 2px rgba(10,37,64,0.28)',
                        }}>
                  <Icon name="play_circle" size={16} color="#fff" />
                  {t('landing.cta_secondary')}
                </button>
              </div>

              {totalLabel && (
                <div style={{
                  display: 'flex', gap: 22, marginTop: 28, flexWrap: 'wrap',
                  color: '#fff', position: 'relative', zIndex: 3,
                }}>
                  <HeroStat big={totalLabel} label={t('landing.stat_restaurants')} />
                </div>
              )}
            </div>

            {/* LivePreview (screens/LandingScreen) is a scripted demo with
                sample restaurants, so it carries an explicit "Example" label. */}
            <div style={{ position: 'relative' }}>
              <LivePreview />
              <span style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                zIndex: 20,
                padding: '4px 12px', borderRadius: 999,
                background: 'rgba(255,255,255,0.95)', color: 'var(--cwm-blue-deep, #0F7FE8)',
                fontSize: 10.5, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase',
                boxShadow: '0 6px 16px rgba(10,37,64,0.12)',
                pointerEvents: 'none',
              }}>
                {t('landing.preview_example')}
              </span>
            </div>
          </div>
        </section>

        {/* ============================================================
            2. WHAT SAFE LOOKS LIKE — featured card + orbiting source badges
            ============================================================ */}
        <BandSection tone="white" padding={BAND_PAD}>
          <Eyebrow>{t('landing.safe_eyebrow')}</Eyebrow>
          <SectionTitle>
            {t('landing.safe_title')}{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('landing.safe_title_em')}</em>
          </SectionTitle>
          <SectionSub>{t('landing.safe_sub')}</SectionSub>

          <SafeProofStage
            restaurant={showcase[0]}
            badges={[
              // All badges live in the CWM Blue family now.
              // Differentiated by weight (pale / mid / deep) — not hue —
              // so the four sources read as ONE palette with intensity
              // variation, not four unrelated colors.
              { key: 'safe',      side: 'left',  y: 38,  copy: t('landing.safe_badge_safe'),      tone: 'deep' },
              { key: 'menu',      side: 'right', y: 62,  copy: t('landing.safe_badge_menu'),      tone: 'mid'  },
              { key: 'community', side: 'left',  y: 250, copy: t('landing.safe_badge_community'), tone: 'mid'  },
              { key: 'ai',        side: 'right', y: 280, copy: t('landing.safe_badge_ai'),        tone: 'pale' },
            ]}
          />

          <div style={{
            maxWidth: 820, margin: '32px auto 0',
            padding: '14px 18px',
            background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
            border: '1.5px solid #F2D08A', borderRadius: 14,
            fontSize: 12.5, fontWeight: 600, lineHeight: 1.55, textAlign: 'center',
          }}>
            <strong>⚠ </strong>{t('landing.safe_disclaimer')}
          </div>
        </BandSection>

        {/* ============================================================
            3. SWIPE TO TRUST IT — live mini-deck
            ============================================================ */}
        <BandSection tone="blue" padding={BAND_PAD}>
          <Eyebrow>{t('landing.deck_eyebrow')}</Eyebrow>
          <SectionTitle>
            {totalLabel ? t('landing.deck_title', { count: totalLabel }) : t('landing.deck_title_fallback')}{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('landing.deck_title_em')}</em>
          </SectionTitle>
          <SectionSub>{t('landing.deck_sub')}</SectionSub>

          {showcase.length > 0 && <MiniDeck restaurants={showcase} />}

          <div style={{ textAlign: 'center', marginTop: 36 }}>
            <button type="button" onClick={() => navigate('/unified/discover')}
                    className="cwm-btn" style={{ padding: '13px 24px', fontSize: 14 }}>
              <Icon name="explore" size={16} color="#fff" />
              {t('landing.deck_cta')}
            </button>
          </div>
        </BandSection>

        {/* ============================================================
            4. BUILT FOR YOUR TABLE — product message. No testimonials
            until we have real, consented ones.
            ============================================================ */}
        <BandSection tone="white" padding={BAND_PAD}>
          <Eyebrow>{t('landing.people_eyebrow')}</Eyebrow>
          <SectionTitle>
            {t('landing.people_title')}{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('landing.people_title_em')}</em>{' '}
            {t('landing.people_title_tail')}
          </SectionTitle>
          <SectionSub>{t('landing.people_sub')}</SectionSub>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button type="button" onClick={onGetStarted}
                    className="cwm-btn" style={{ padding: '13px 24px', fontSize: 14 }}>
              <Icon name="explore" size={16} color="#fff" />
              {t('landing.people_cta')}
            </button>
          </div>
        </BandSection>

        {/* ============================================================
            5. FREE FOREVER — side-by-side pricing peeks
            ============================================================ */}
        <BandSection tone="blue" padding={BAND_PAD}>
          <Eyebrow>{t('landing.peek_eyebrow')}</Eyebrow>
          <SectionTitle>
            {t('landing.peek_title')}{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('landing.peek_title_em')}</em>
          </SectionTitle>
          <SectionSub>{t('landing.peek_sub')}</SectionSub>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 22,
            marginTop: 48, maxWidth: 940, marginLeft: 'auto', marginRight: 'auto',
          }}>
            <PricingPeek
              side="left"
              label={t('landing.peek_diner')}
              price={t('landing.peek_diner_line')}
              hint={t('landing.peek_diner_hint')}
              cta={t('landing.peek_diner_cta')}
              onPick={() => navigate('/unified/pricing')}
              icon="user"
            />
            <PricingPeek
              side="right"
              label={t('landing.peek_rest')}
              price={t('landing.peek_rest_line')}
              hint={t('landing.peek_rest_hint')}
              cta={t('landing.peek_rest_cta')}
              onPick={() => navigate('/unified/restaurant-pricing')}
              icon="building"
            />
          </div>
        </BandSection>

        {/* ============================================================
            6. FINAL CTA — gradient band
            ============================================================ */}
        <section style={{ background: 'transparent', padding: '40px 48px 88px', position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.56, ease: EASE }}
            style={{
              maxWidth: 920, margin: '0 auto',
              borderRadius: 28, padding: '52px 32px',
              background: 'linear-gradient(160deg, #1E9BFF 0%, #0F7FE8 55%, #0A2540 100%)',
              boxShadow: '0 28px 72px rgba(10,37,64,0.32)',
              color: '#fff', textAlign: 'center',
              position: 'relative', overflow: 'hidden',
            }}
          >
            <div aria-hidden style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
              backgroundSize: '22px 22px', opacity: 0.3,
            }}/>
            <div style={{ position: 'relative' }}>
              <h2 style={{ margin: '0 0 10px', fontSize: 36, fontWeight: 900, letterSpacing: '-0.02em' }}>
                {t('landing.final_title')} <em style={{ fontStyle: 'italic' }}>{t('landing.final_title_em')}</em>
              </h2>
              <p style={{ margin: '0 auto 24px', fontSize: 15, fontWeight: 600, opacity: 0.95, maxWidth: 520 }}>
                {t('landing.final_sub')}
              </p>
              <div style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button type="button" onClick={onGetStarted}
                        style={{
                          padding: '15px 32px', borderRadius: 999,
                          background: '#fff', color: 'var(--cwm-blue-deep)',
                          border: 'none', cursor: 'pointer',
                          fontFamily: 'var(--cwm-font)', fontSize: 15, fontWeight: 900,
                          boxShadow: '0 10px 26px rgba(10,37,64,0.25)',
                        }}>
                  {t('landing.final_cta_primary')}
                </button>
                <button type="button" onClick={() => navigate('/unified/for-restaurants')}
                        style={{
                          padding: '15px 24px', borderRadius: 999,
                          background: 'rgba(255,255,255,0.14)', color: '#fff',
                          border: '1.5px solid rgba(255,255,255,0.45)',
                          cursor: 'pointer', backdropFilter: 'blur(6px)',
                          fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 800,
                        }}>
                  {t('landing.final_cta_secondary')} →
                </button>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} onGetStarted={onGetStarted} />
    </AppShell>
  );
}

/* ============================================================================
   SAFE PROOF STAGE
   Featured real restaurant card with source badges that animate in and connect
   to the card by a thin line. This is the "sourced" story made visible.
============================================================================ */

function SafeProofStage({ badges, restaurant }) {
  // The featured card is a real restaurant from the directory. Until it
  // loads (or if it can't), the stage is skipped entirely.
  if (!restaurant) return null;

  return (
    <div style={{
      position: 'relative',
      maxWidth: 1040,
      margin: '48px auto 0',
      minHeight: 480,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
    }} className="cwm-safe-stage">
      {/* Floating badges — absolute positioned around the centered card */}
      {badges.map((b, i) => (
        <motion.div
          key={b.key}
          initial={{ opacity: 0, x: b.side === 'left' ? -30 : 30, y: 0 }}
          whileInView={{ opacity: 1, x: 0, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.56, delay: 0.25 + i * 0.12, ease: EASE }}
          style={{
            position: 'absolute',
            top: b.y,
            [b.side]: 'calc(50% + 210px)',
            zIndex: 3,
          }}
          className="cwm-safe-badge"
        >
          <SourceBadge tone={b.tone} side={b.side}>{b.copy}</SourceBadge>
        </motion.div>
      ))}

      {/* Centered featured card with a soft halo */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7, ease: EASE }}
        style={{ position: 'relative', zIndex: 2 }}
      >
        <div aria-hidden style={{
          position: 'absolute',
          inset: -30,
          background: 'radial-gradient(closest-side, rgba(30,155,255,0.22), rgba(30,155,255,0) 70%)',
          filter: 'blur(20px)',
          zIndex: -1,
        }}/>
        <RealRestaurantCard r={restaurant} />
      </motion.div>
    </div>
  );
}

// All badges live in the CWM Blue family — differentiated by intensity
// (pale / mid / deep) so the group reads as one palette with depth,
// not four competing colors. Halo + dot use the same blue value as the
// border so there's only ONE hue per badge.
function SourceBadge({ tone = 'mid', side = 'left', children }) {
  const palette = {
    pale: { bg: 'var(--cwm-blue-pale, #E5F3FF)',  fg: 'var(--cwm-blue-deep, #0F7FE8)', dot: '#7FBFF5' },
    mid:  { bg: 'var(--cwm-blue-light, #BFE5FF)', fg: 'var(--cwm-blue-deep, #0F7FE8)', dot: '#1E9BFF' },
    deep: { bg: 'var(--cwm-blue-deep, #0F7FE8)',  fg: '#fff',                          dot: '#0A2540' },
  }[tone];
  // `deep` tone renders as a filled blue pill (solid brand blue);
  // `pale` and `mid` render as white cards with a blue-tinted border.
  const isDeep = tone === 'deep';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '9px 14px', borderRadius: 999,
      background: isDeep ? palette.bg : '#fff',
      border: `1.5px solid ${isDeep ? palette.bg : palette.bg}`,
      color: palette.fg,
      fontSize: 12.5, fontWeight: 800, letterSpacing: '-0.005em',
      boxShadow: '0 10px 22px rgba(10,37,64,0.1)',
      position: 'relative',
    }}>
      {/* Connector dot on the card-facing side */}
      <span aria-hidden style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [side === 'left' ? 'right' : 'left']: -18,
        width: 10, height: 10, borderRadius: 999,
        background: palette.dot,
        boxShadow: `0 0 0 4px rgba(229,243,255,0.88)`,
      }}/>
      <span style={{
        width: 8, height: 8, borderRadius: 999,
        background: isDeep ? '#fff' : palette.dot,
      }}/>
      {children}
    </div>
  );
}

/* ============================================================================
   REAL RESTAURANT CARD
   Same footprint as the old demo card, but rendered only from fields the
   /listRestaurants response actually carries: name, cuisine, price range,
   rating, dietary tags, description and photo.
============================================================================ */

function displayTags(r) {
  const raw = r.dietary_display_tags;
  let arr = raw;
  if (typeof raw === 'string') {
    try { arr = JSON.parse(raw); } catch { arr = []; }
  }
  return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string' && x.trim()) : [];
}

// Prefer rows that make a complete card (rating + tags), most-reviewed
// first. Deterministic so the page doesn't reshuffle on every render.
function pickShowcase(rows) {
  const scored = rows
    .filter((r) => r && r.name)
    .map((r) => ({
      r,
      score: (Number(r.display_rating) > 0 ? 2 : 0) + (displayTags(r).length > 0 ? 2 : 0) + (r.description ? 1 : 0),
      reviews: Number(r.display_review_count) || 0,
    }))
    .sort((a, b) => (b.score - a.score) || (b.reviews - a.reviews));
  return scored.slice(0, 8).map((x) => x.r);
}

function RealRestaurantCard({ r }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = r.photo_url || restaurantPhotoUrl(r.id, 0);
  const rating = Number(r.display_rating);
  const tags = displayTags(r).slice(0, 4);
  const meta = [r.cuisine_type, r.price_range].filter(Boolean).join(' · ');
  return (
    <div style={{
      width: 330, height: 440,
      background: '#fff',
      borderRadius: 24,
      overflow: 'hidden',
      boxShadow: 'var(--cwm-shadow-hero)',
      border: '1px solid rgba(255,255,255,0.9)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        height: 200,
        background: 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
        position: 'relative',
        flexShrink: 0,
      }}>
        {!photoFailed && (
          <img
            src={photo}
            alt=""
            onError={() => setPhotoFailed(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {rating > 0 && (
          <div style={{ position: 'absolute', top: 12, right: 12 }}>
            <Rating value={rating.toFixed(1)} />
          </div>
        )}
      </div>
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 20, letterSpacing: '-0.01em', color: 'var(--cwm-ink)' }}>{r.name}</div>
          {meta && (
            <div style={{ fontSize: 12, color: 'var(--cwm-gray-500)', fontWeight: 600, marginTop: 2 }}>{meta}</div>
          )}
        </div>
        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {tags.map((label) => {
              const pal = paletteForTag(label);
              return (
                <span key={label} className="cwm-tag" style={{ background: pal.bg, color: pal.fg, border: `1px solid ${pal.border}` }}>
                  {label}
                </span>
              );
            })}
          </div>
        )}
        {r.description && (
          <div style={{
            fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.5, marginTop: 4,
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {r.description}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
   MINI DECK
   Auto-cycling stack of real restaurant cards. Each card tilts, flies off, and
   the next one pulls forward. Same primitive as the hero's LivePreview
   but presented center-stage with stronger motion.
============================================================================ */

function MiniDeck({ restaurants }) {
  const deck = restaurants;
  const [top, setTop] = useState(0);
  const [phase, setPhase] = useState('idle');
  const [dir, setDir] = useState(1);

  useEffect(() => {
    let alive = true;
    let t1, t2, t3;
    const loop = () => {
      if (!alive) return;
      const nextDir = Math.random() > 0.3 ? 1 : -1;
      setDir(nextDir);
      t1 = setTimeout(() => { if (alive) setPhase('tilt'); }, 120);
      t2 = setTimeout(() => { if (alive) setPhase('fly'); }, 1600);
      t3 = setTimeout(() => {
        if (!alive) return;
        setTop((prev) => (prev + 1) % deck.length);
        setPhase('idle');
        loop();
      }, 2300);
    };
    loop();
    return () => {
      alive = false;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const order = [0, 1, 2].map((i) => deck[(top + i) % deck.length]);

  return (
    <div style={{
      position: 'relative',
      width: 340,
      height: 470,
      margin: '48px auto 0',
    }}>
      {order.slice().reverse().map((r, rev) => {
        const i = order.length - 1 - rev;
        const isTop = i === 0;
        const baseLift = i * 10;
        const baseScale = 1 - i * 0.05;

        let transform = `translateY(${baseLift}px) scale(${baseScale})`;
        let opacity = 1;
        if (isTop) {
          if (phase === 'tilt') transform = `translateX(${dir * 70}px) rotate(${dir * 11}deg)`;
          if (phase === 'fly') {
            transform = `translateX(${dir * 620}px) rotate(${dir * 24}deg)`;
            opacity = 0;
          }
        }

        return (
          <div
            key={`${r.id}-${top}-${i}`}
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              transform,
              opacity,
              transition: isTop
                ? phase === 'fly'
                  ? 'transform 720ms cubic-bezier(.5,.1,.8,.2), opacity 620ms ease-out'
                  : 'transform 600ms cubic-bezier(.2,.9,.3,1.1)'
                : 'transform 420ms ease-out',
              zIndex: 10 - i,
              pointerEvents: 'none',
            }}
          >
            <div style={{ position: 'relative' }}>
              {isTop && phase !== 'idle' && (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: -14,
                    borderRadius: 34,
                    background:
                      dir === 1
                        ? 'radial-gradient(closest-side, rgba(22,128,60,0.85), rgba(22,128,60,0) 75%)'
                        : 'radial-gradient(closest-side, rgba(220,38,38,0.85), rgba(220,38,38,0) 75%)',
                    filter: 'blur(22px)',
                    opacity: phase === 'fly' ? 0 : 1,
                    transition: 'opacity 350ms ease-out',
                    zIndex: -1,
                  }}
                />
              )}
              <RealRestaurantCard r={r} />
              {isTop && phase !== 'idle' && (
                <div
                  style={{
                    position: 'absolute',
                    top: 32,
                    [dir === 1 ? 'right' : 'left']: 18,
                    padding: '7px 16px',
                    border: `3px solid ${dir === 1 ? 'var(--cwm-green)' : 'var(--cwm-skip)'}`,
                    borderRadius: 10,
                    color: dir === 1 ? 'var(--cwm-green)' : 'var(--cwm-skip)',
                    fontWeight: 900,
                    fontSize: 24,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    transform: `rotate(${dir === 1 ? 14 : -14}deg)`,
                    background: 'rgba(255,255,255,0.96)',
                    opacity: phase === 'tilt' ? 0.96 : 0.85,
                    transition: 'opacity 200ms',
                  }}
                >
                  {dir === 1 ? 'Save' : 'Skip'}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================================
   PRICING PEEK
   One per audience (diner / restaurant). Big number arc, hover lift, clear CTA.
============================================================================ */

function PricingPeek({ side, label, price, hint, cta, onPick, icon }) {
  const isLeft = side === 'left';
  return (
    <motion.button
      type="button"
      onClick={onPick}
      initial={{ opacity: 0, x: isLeft ? -24 : 24 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.56, ease: EASE }}
      whileHover={{ y: -6, boxShadow: '0 28px 60px rgba(10,37,64,0.22)' }}
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        border: '1.5px solid var(--cwm-blue-light)',
        borderRadius: 24,
        background: '#fff',
        padding: '26px 26px 22px',
        fontFamily: 'var(--cwm-font)',
        boxShadow: '0 16px 36px rgba(10,37,64,0.08)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 240ms ease',
      }}
    >
      {/* Corner icon */}
      <div style={{
        position: 'absolute',
        top: 22, right: 22,
        width: 42, height: 42, borderRadius: 14,
        background: 'var(--cwm-blue-pale)',
        color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center',
      }}>
        <PeekIcon name={icon} size={22} />
      </div>

      <div style={{
        fontSize: 11, fontWeight: 900, letterSpacing: '0.14em',
        color: 'var(--cwm-blue-deep)',
        textTransform: 'uppercase',
        marginBottom: 10,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 24, fontWeight: 900, letterSpacing: '-0.025em',
        color: 'var(--cwm-ink)',
        lineHeight: 1.1,
      }}>
        {price}
      </div>
      <p style={{
        margin: '12px 0 20px', fontSize: 13.5, fontWeight: 500, lineHeight: 1.55,
        color: 'var(--cwm-gray-700)',
      }}>
        {hint}
      </p>

      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '10px 18px', borderRadius: 999,
        background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)',
        color: '#fff',
        fontSize: 13, fontWeight: 900,
        boxShadow: '0 8px 18px rgba(15,127,232,0.3)',
      }}>
        {cta} →
      </div>
    </motion.button>
  );
}

function PeekIcon({ name, size = 20 }) {
  const paths = {
    user:     'M12 12a4 4 0 100-8 4 4 0 000 8zM4 20c0-4 4-6 8-6s8 2 8 6',
    building: 'M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16M9 7h0M9 11h0M9 15h0M14 7h0M14 11h0M14 15h0M5 21h14',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name] || paths.user} />
    </svg>
  );
}

/* ============================================================================
   LAYOUT HELPERS
============================================================================ */

// Section backgrounds are INTENTIONALLY translucent so the page-wide
// mascot watermark (rendered by AppShell) bleeds through every section,
// not just the hero. Tuned alpha — high enough that body text reads
// crisp, low enough that the mascots show as a faint brand presence.
// No backdrop-filter: added perceptible softness that was reading as
// "draft/prototype" instead of "polished."
function BandSection({ children, tone = 'white', padding = BAND_PAD }) {
  const bg = tone === 'blue'
    ? 'rgba(229,243,255,0.88)'
    : 'rgba(255,255,255,0.88)';
  return (
    <section style={{ background: bg, padding, position: 'relative', zIndex: 2 }}>
      {children}
    </section>
  );
}

function Eyebrow({ children, tone }) {
  const color = tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-blue-deep)';
  return (
    <div style={{
      textAlign: 'center', fontSize: 11, fontWeight: 900,
      letterSpacing: '0.16em', textTransform: 'uppercase',
      color, marginBottom: 10,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      textAlign: 'center', margin: '0 auto', maxWidth: 820,
      fontSize: 44, fontWeight: 900, letterSpacing: '-0.025em', lineHeight: 1.08,
      color: 'var(--cwm-ink)',
    }} className="cwm-land-section-h2">
      {children}
    </h2>
  );
}

function SectionSub({ children, size = 16, maxWidth = 640 }) {
  return (
    <p style={{
      textAlign: 'center', margin: '14px auto 0',
      maxWidth, fontSize: size, fontWeight: 500, lineHeight: 1.55,
      color: 'var(--cwm-gray-700)',
    }}>
      {children}
    </p>
  );
}

function HeroStat({ big, label, stars }) {
  return (
    <div style={{ color: '#fff' }}>
      <div style={{
        fontSize: 26, fontWeight: 900, letterSpacing: '-0.015em',
        textShadow: '0 1px 4px rgba(10,37,64,0.35)',
        display: 'inline-flex', alignItems: 'baseline', gap: 6,
      }}>
        {stars && <span aria-hidden style={{ color: '#FFD66B', fontSize: 22, lineHeight: 1 }}>★</span>}
        <span>{big}</span>
      </div>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.95)',
        textShadow: '0 1px 2px rgba(10,37,64,0.3)',
      }}>
        {label}
      </div>
    </div>
  );
}


/* ============================================================================
   LIVE PILLS
   Replaces the old static FloatingTags. Dietary pills now have a real
   lifecycle: spawn at the bottom of the hero → drift upward with a gentle
   sway → pop (scale up + fade) at the top → a new pill takes its place.
   Caps active pills at ~4 so the hero stays uncluttered — the FEEL is
   "the filters are alive," not "there's a wall of stickers."
============================================================================ */

// Pool of dietary labels. Each pill renders in the color of the taxonomy
// group it belongs to — Allergies in red, Diet in green, Religious in
// indigo, Therapeutic in teal, Lifestyle in blue, GLP-1 in amber, etc.
// So a diner who's used the app sees "Gluten-Free" floating in the same
// red they filter by on the discovery screen. Color consistency across
// the whole product. `paletteForTag(label)` is the shared lookup.
//
// NOTE: the labels below must be canonical taxonomy entries (TAG_GROUPS
// in features/discovery/taxonomy.js) so the palette lookup resolves.
// "Ozempic-Friendly" is the canonical GLP-1 label; "GLP-1" alone isn't
// in the taxonomy, so we spell it out.
const PILL_POOL = [
  'Gluten-Free',  'Dairy-Free', 'Nut-Free',     // Allergies (red)
  'Vegan',        'Vegetarian', 'Pescatarian',  // Diet (green)
  'Keto',         'Low-Carb',                   // Diet (green)
  'Kosher',       'Halal',                      // Religious (indigo)
  'Low-Sodium',                                 // Therapeutic (teal)
  'High-Protein', 'Sugar-Free',                 // Lifestyle (blue)
  'Ozempic-Friendly',                           // GLP-1 / Portions (amber)
];

// Cap on concurrent pills. 3 keeps the hero feeling alive without ever
// looking crowded — at any given frame there's one rising, one mid-
// flight, one about to pop.
const PILL_CAP = 3;
// How often to try to spawn a new pill (ms). Slower than before (1600ms)
// so the pills feel deliberate, not a constant shower.
const PILL_SPAWN_EVERY = 2400;

function LivePills() {
  const [pills, setPills] = useState([]);
  const nextId = useRef(0);
  // Alternate which edge band each new pill lands in. Starts as 'right'
  // so the first spawn is 'left' — doesn't matter which, just deterministic
  // so back-to-back pills can never both pick the same side and stack.
  const lastSide = useRef('right');

  // Build a pill with a fresh id + randomized trajectory params. Pulled
  // into a ref-closure so the interval below never captures a stale copy.
  //
  // `activeLabels` — labels currently on screen. We filter them out of
  // the random pool so the hero never shows two identical pills at the
  // same time (previously: "Kosher" could stack under "Kosher").
  const makeOne = (activeLabels = []) => {
    const id = nextId.current++;
    const available = PILL_POOL.filter((l) => !activeLabels.includes(l));
    const pool = available.length ? available : PILL_POOL;
    const label = pool[Math.floor(Math.random() * pool.length)];

    // Flip side for every spawn. Back-to-back pills are always on
    // opposite edges of the hero — no more clumping.
    const side = lastSide.current === 'left' ? 'right' : 'left';
    lastSide.current = side;
    const x = side === 'left'
      ? 4 + Math.random() * 12   // left band 4–16%
      : 84 + Math.random() * 12; // right band 84–96%

    return {
      id,
      label,
      // `palette` is the shared taxonomy palette for the label's group
      // (Allergies=red, Diet=green, Religious=indigo, …). Same colors
      // that show up on restaurant cards + filter drawer elsewhere in
      // the product, so the hero pills feel like part of the app.
      palette: paletteForTag(label),
      x,
      rot:      -10 + Math.random() * 20,
      duration: 8 + Math.random() * 2, // 8–10s full rise (calmer)
      size:     12 + Math.random() * 2,
      sway:     -8 + Math.random() * 16,
    };
  };

  useEffect(() => {
    // Seed just ONE pill on mount. The old code seeded two at once,
    // which could land them both at bottom=0 with similar x values —
    // creating the "pills stacked on page load" artifact. With a single
    // seed + interval-driven spawns, every subsequent pill enters at a
    // different lifecycle phase from the last one, so overlap is naturally
    // prevented.
    setPills([makeOne([])]);

    const tick = setInterval(() => {
      setPills((curr) => {
        if (curr.length >= PILL_CAP) return curr;
        const activeLabels = curr.map((p) => p.label);
        return [...curr, makeOne(activeLabels)];
      });
    }, PILL_SPAWN_EVERY);

    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = (id) => setPills((curr) => curr.filter((p) => p.id !== id));

  return (
    <div aria-hidden style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      overflow: 'hidden',
    }}>
      <AnimatePresence>
        {pills.map((p) => (
          <LivePill key={p.id} pill={p} onDone={() => remove(p.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function LivePill({ pill, onDone }) {
  // Taxonomy-tinted pill. Uses the inactive-pill style from the shared
  // palette (soft tinted bg + deeper text + matching border) — identical
  // treatment to the pills on a restaurant card, so the hero reads as a
  // live preview of the product's real filter language.
  const { bg, fg, border } = pill.palette;
  return (
    <motion.span
      initial={{
        y: '110%',
        x: 0,
        opacity: 0,
        scale: 0.6,
        rotate: pill.rot,
      }}
      animate={{
        y: '-120%',
        x: pill.sway,
        opacity: [0, 0.95, 0.95, 0],
        scale: [0.6, 1, 1, 1.6],
        rotate: pill.rot + (pill.sway > 0 ? 8 : -8),
      }}
      transition={{
        y:       { duration: pill.duration, ease: 'linear' },
        x:       { duration: pill.duration, ease: 'easeInOut' },
        rotate:  { duration: pill.duration, ease: 'easeInOut' },
        opacity: { duration: pill.duration, ease: 'linear',   times: [0, 0.1, 0.85, 1] },
        scale:   { duration: pill.duration, ease: 'easeOut',  times: [0, 0.1, 0.85, 1] },
      }}
      onAnimationComplete={onDone}
      style={{
        position: 'absolute',
        left: `${pill.x}%`,
        bottom: 0,
        padding: '6px 12px',
        borderRadius: 999,
        background: bg,
        color:      fg,
        border:     `1px solid ${border}`,
        fontFamily: 'var(--cwm-font)',
        fontWeight: 800,
        fontSize: pill.size,
        boxShadow: '0 6px 16px rgba(10,37,64,0.1)',
        whiteSpace: 'nowrap',
        willChange: 'transform, opacity',
      }}
    >
      {pill.label}
    </motion.span>
  );
}

/* ============================================================================
   RESPONSIVE CSS
============================================================================ */

const landingResponsiveCss = `
  @media (max-width: 900px) {
    .cwm-land-hero { padding: 20px 20px 56px !important; }
    .cwm-land-hero-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
    .cwm-land-h1 { font-size: 44px !important; }
  }
  @media (max-width: 760px) {
    .cwm-safe-stage { min-height: auto !important; flex-direction: column !important; align-items: center !important; gap: 16px; }
    .cwm-safe-badge {
      position: relative !important;
      top: auto !important;
      left: auto !important;
      right: auto !important;
    }
  }
  @media (max-width: 560px) {
    .cwm-land-h1 { font-size: 36px !important; }
    .cwm-land-section-h2 { font-size: 30px !important; }
  }
`;
